import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Box, Button, FormControl, Grid, InputLabel, MenuItem, Paper, Select, TextField, Typography } from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { useNavigate } from 'react-router-dom';
import { showDefaultAlert } from '../utils/Alerts';
import { UserContext } from '../contexts/UserContext';
import {
  addDealerBranding,
  getDealerBrandingPreviewUrlFromKey,
  getDealerBrandingUploadUrl,
  getDealerBrandingsByDealerID,
} from '../services/DealerBrandingAPI';

const fontPresets = [
  { label: 'Poppins (Default)', value: 'Poppins, sans-serif' },
  { label: 'Montserrat', value: 'Montserrat, sans-serif' },
  { label: 'Open Sans', value: '"Open Sans", sans-serif' },
  { label: 'Lato', value: 'Lato, sans-serif' },
];

const colorFields = [
  { key: 'primary', label: 'Primary' },
  { key: 'secondary', label: 'Secondary' },
  { key: 'accent', label: 'Accent' },
  { key: 'background', label: 'Background' },
  { key: 'surface', label: 'Surface' },
  { key: 'text', label: 'Text' },
];

const createDefaultFormData = () => ({
  colors: {
    primary: '#C52B2B',
    secondary: '#FFFFFF',
    accent: '#A82222',
    background: '#FFFFFF',
    surface: '#F8FAFC',
    text: '#111111',
  },
  typography: {
    heading: 'Poppins, sans-serif',
    body: 'Poppins, sans-serif',
  },
  branding: {
    name: '',
    tagline: '',
    phone: '',
    email: '',
    address: '',
    customDomain: '',
    logo: '',
  },
  heroImage: '',
});

const createInitialColorErrors = () => colorFields.reduce((acc, field) => {
  acc[field.key] = '';
  return acc;
}, {});

const getApiErrorMessage = (error) => {
  const responseData = error?.response?.data;

  if (typeof responseData === 'string') {
    return responseData;
  }

  if (responseData && typeof responseData === 'object') {
    if (typeof responseData.message === 'string') {
      return responseData.message;
    }
    return JSON.stringify(responseData);
  }

  if (typeof error?.message === 'string') {
    return error.message;
  }

  return 'Failed to process branding request.';
};

const toNonEmptyString = (value) => {
  if (typeof value === 'string') {
    return value.trim().length > 0 ? value.trim() : '';
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }

  return '';
};

const pickFirstString = (...values) => {
  for (let index = 0; index < values.length; index += 1) {
    const formattedValue = toNonEmptyString(values[index]);
    if (formattedValue) {
      return formattedValue;
    }
  }
  return '';
};

const isHttpUrl = (value) => /^https?:\/\//i.test(value);

const getPreviewableUrl = (value) => {
  const formattedValue = toNonEmptyString(value);
  return isHttpUrl(formattedValue) ? formattedValue : '';
};

const extractImageKey = (value) => {
  const formattedValue = toNonEmptyString(value);
  if (!formattedValue) {
    return '';
  }

  if (!isHttpUrl(formattedValue)) {
    return formattedValue;
  }

  try {
    const parsedUrl = new URL(formattedValue);
    const segments = decodeURIComponent(parsedUrl.pathname || '')
      .split('/')
      .filter(Boolean);

    if (!segments.length) {
      return '';
    }

    const host = (parsedUrl.hostname || '').toLowerCase();
    const isPathStyleS3Host = host === 's3.amazonaws.com'
      || host.startsWith('s3.')
      || host.startsWith('s3-');

    if (isPathStyleS3Host && segments.length > 1) {
      return segments.slice(1).join('/');
    }

    return segments.join('/');
  } catch (error) {
    return formattedValue;
  }
};

const getLogoPreviewUrlFromRecord = (brandingRecord) => getPreviewableUrl(pickFirstString(
  brandingRecord?.logoPreviewUrl,
  brandingRecord?.logoImageUrl,
  brandingRecord?.logoURL,
  brandingRecord?.logoUrl
));

const getHeroPreviewUrlFromRecord = (brandingRecord) => getPreviewableUrl(pickFirstString(
  brandingRecord?.heroImagePreviewUrl,
  brandingRecord?.heroPreviewUrl,
  brandingRecord?.heroImageFullUrl,
  brandingRecord?.heroImage,
  brandingRecord?.heroImageUrl
));

const resolveExistingImagePreviewUrl = async (explicitPreviewUrl, storedImageValue, dealerID, adminID, token) => {
  if (explicitPreviewUrl) {
    return explicitPreviewUrl;
  }

  const normalizedStoredValue = toNonEmptyString(storedImageValue);
  if (!normalizedStoredValue) {
    return '';
  }

  try {
    return await getDealerBrandingPreviewUrlFromKey(normalizedStoredValue, dealerID, adminID, token);
  } catch (error) {
    return '';
  }
};

const normalizeHexColor = (value, fallback) => {
  const formattedValue = toNonEmptyString(value).toUpperCase();
  if (!formattedValue) {
    return fallback;
  }

  const withHash = formattedValue.startsWith('#') ? formattedValue : `#${formattedValue}`;
  return /^#[0-9A-F]{6}$/.test(withHash) ? withHash : fallback;
};

const isLikelyBrandingRecord = (record) => {
  if (!record || typeof record !== 'object') {
    return false;
  }

  return Boolean(
    record.brandName
      || record.logoUrl
      || record.heroImageUrl
      || record.phoneNo
      || record.primaryColor
      || record.secondaryColor
      || record.accentColor
      || record.backgroundColor
      || record.textColor
      || record.surfacecolor
  );
};

const getBrandingRecords = (response) => {
  const responseData = response?.data;
  let records = [];

  if (Array.isArray(responseData)) {
    records = responseData;
  } else if (responseData && typeof responseData === 'object') {
    if (Array.isArray(responseData.data)) {
      records = responseData.data;
    } else if (Array.isArray(responseData.items)) {
      records = responseData.items;
    } else if (responseData.data && typeof responseData.data === 'object') {
      if (Array.isArray(responseData.data.items)) {
        records = responseData.data.items;
      } else {
        records = [responseData.data];
      }
    } else {
      records = [responseData];
    }
  }

  return records.filter(isLikelyBrandingRecord);
};

const getBrandingTimestamp = (branding) => {
  const rawTimestamp = branding?.updatedAt ?? branding?.createdAt ?? 0;
  const numericTimestamp = Number(rawTimestamp);

  if (!Number.isFinite(numericTimestamp) || numericTimestamp <= 0) {
    return 0;
  }

  return numericTimestamp < 1000000000000 ? numericTimestamp * 1000 : numericTimestamp;
};

const getLatestBrandingRecord = (brandings) => {
  if (!Array.isArray(brandings) || brandings.length === 0) {
    return null;
  }

  return [...brandings].sort((a, b) => getBrandingTimestamp(b) - getBrandingTimestamp(a))[0];
};

const mapBrandingRecordToFormData = (brandingRecord) => {
  const defaultForm = createDefaultFormData();

  if (!brandingRecord || typeof brandingRecord !== 'object') {
    return defaultForm;
  }

  return {
    colors: {
      primary: normalizeHexColor(brandingRecord.primaryColor, defaultForm.colors.primary),
      secondary: normalizeHexColor(brandingRecord.secondaryColor, defaultForm.colors.secondary),
      accent: normalizeHexColor(brandingRecord.accentColor, defaultForm.colors.accent),
      background: normalizeHexColor(brandingRecord.backgroundColor, defaultForm.colors.background),
      surface: normalizeHexColor(brandingRecord.surfacecolor, defaultForm.colors.surface),
      text: normalizeHexColor(brandingRecord.textColor, defaultForm.colors.text),
    },
    typography: {
      heading: pickFirstString(
        brandingRecord.headingFont,
        defaultForm.typography.heading
      ),
      body: pickFirstString(
        brandingRecord.bodyFont,
        defaultForm.typography.body
      ),
    },
    branding: {
      name: pickFirstString(
        brandingRecord.brandName
      ),
      tagline: pickFirstString(
        brandingRecord.tagline
      ),
      phone: pickFirstString(
        brandingRecord.phoneNo
      ),
      email: pickFirstString(
        brandingRecord.email
      ),
      address: pickFirstString(
        brandingRecord.address
      ),
      customDomain: pickFirstString(
        brandingRecord.customDomain
      ),
      logo: pickFirstString(
        brandingRecord.logoUrl
      ),
    },
    heroImage: pickFirstString(
      brandingRecord.heroImageUrl
    ),
  };
};

const DealerBrandingPage = () => {
  const { user, token } = useContext(UserContext);
  const navigate = useNavigate();
  const initialForm = createDefaultFormData();
  const [formData, setFormData] = useState(initialForm);
  const [errorMessage, setErrorMessage] = useState('');
  const [colorHexInputs, setColorHexInputs] = useState(initialForm.colors);
  const [colorInputErrors, setColorInputErrors] = useState(createInitialColorErrors);
  const [isFetchingBranding, setIsFetchingBranding] = useState(false);
  const [hasExistingBranding, setHasExistingBranding] = useState(false);
  const [imageUploadState, setImageUploadState] = useState({
    logo: { isUploading: false, fileName: '' },
    hero: { isUploading: false, fileName: '' },
  });
  const [imagePreviewUrls, setImagePreviewUrls] = useState({
    logo: '',
    hero: '',
  });
  const localObjectUrlsRef = useRef({
    logo: '',
    hero: '',
  });

  const hexInputPattern = /^#?[0-9A-Fa-f]{0,6}$/;
  const fullHexPattern = /^#[0-9A-Fa-f]{6}$/;

  const inputSx = {
    '& .MuiInputLabel-root.Mui-focused': { color: '#4b5563' },
    '& .MuiOutlinedInput-root.Mui-focused fieldset': { borderColor: '#9ca3af' },
  };

  const revokeLocalPreviewUrl = useCallback((imageType) => {
    const currentUrl = localObjectUrlsRef.current[imageType];
    if (currentUrl) {
      URL.revokeObjectURL(currentUrl);
      localObjectUrlsRef.current[imageType] = '';
    }
  }, []);

  const clearAllLocalPreviewUrls = useCallback(() => {
    revokeLocalPreviewUrl('logo');
    revokeLocalPreviewUrl('hero');
  }, [revokeLocalPreviewUrl]);

  const resetForm = useCallback(() => {
    const freshForm = createDefaultFormData();
    clearAllLocalPreviewUrls();
    setFormData(freshForm);
    setErrorMessage('');
    setColorHexInputs(freshForm.colors);
    setColorInputErrors(createInitialColorErrors());
    setImageUploadState({
      logo: { isUploading: false, fileName: '' },
      hero: { isUploading: false, fileName: '' },
    });
    setImagePreviewUrls({
      logo: '',
      hero: '',
    });
  }, [clearAllLocalPreviewUrls]);

  useEffect(() => () => {
    clearAllLocalPreviewUrls();
  }, [clearAllLocalPreviewUrls]);

  useEffect(() => {
    const adminID = user?.UserID ?? user?.userID;
    const dealerID = user?.DealerID ?? user?.dealerID;

    if (!adminID || !dealerID || !token) {
      return;
    }

    let isActive = true;

    const fetchExistingBranding = async () => {
      setIsFetchingBranding(true);
      try {
        const response = await getDealerBrandingsByDealerID(adminID, dealerID, token);
        if (!isActive) {
          return;
        }

        const brandingRecords = getBrandingRecords(response);
        const latestBranding = getLatestBrandingRecord(brandingRecords);

        if (latestBranding) {
          const mappedFormData = mapBrandingRecordToFormData(latestBranding);
          const [logoPreviewUrl, heroPreviewUrl] = await Promise.all([
            resolveExistingImagePreviewUrl(
              getLogoPreviewUrlFromRecord(latestBranding),
              mappedFormData.branding.logo,
              dealerID,
              adminID,
              token
            ),
            resolveExistingImagePreviewUrl(
              getHeroPreviewUrlFromRecord(latestBranding),
              mappedFormData.heroImage,
              dealerID,
              adminID,
              token
            ),
          ]);

          if (!isActive) {
            return;
          }

          clearAllLocalPreviewUrls();
          setFormData(mappedFormData);
          setColorHexInputs(mappedFormData.colors);
          setColorInputErrors(createInitialColorErrors());
          setImageUploadState({
            logo: { isUploading: false, fileName: '' },
            hero: { isUploading: false, fileName: '' },
          });
          setImagePreviewUrls({
            logo: logoPreviewUrl,
            hero: heroPreviewUrl,
          });
          setHasExistingBranding(true);
          setErrorMessage('');
          return;
        }

        resetForm();
        setHasExistingBranding(false);
      } catch (error) {
        if (!isActive) {
          return;
        }
        setErrorMessage(getApiErrorMessage(error));
      } finally {
        if (isActive) {
          setIsFetchingBranding(false);
        }
      }
    };

    fetchExistingBranding();

    return () => {
      isActive = false;
    };
  }, [clearAllLocalPreviewUrls, resetForm, token, user?.UserID, user?.userID, user?.DealerID, user?.dealerID]);

  const handleBrandingInputChange = (field) => (event) => {
    const value = event.target.value;
    setErrorMessage('');
    setFormData((prev) => ({
      ...prev,
      branding: {
        ...prev.branding,
        [field]: value,
      },
    }));
  };

  const handleTypographyChange = (field) => (event) => {
    const value = event.target.value;
    setErrorMessage('');
    setFormData((prev) => ({
      ...prev,
      typography: {
        ...prev.typography,
        [field]: value,
      },
    }));
  };

  const handleColorPickerChange = (colorKey) => (event) => {
    const newColor = event.target.value.toUpperCase();
    setErrorMessage('');
    setFormData((prev) => ({
      ...prev,
      colors: {
        ...prev.colors,
        [colorKey]: newColor,
      },
    }));

    setColorHexInputs((prev) => ({
      ...prev,
      [colorKey]: newColor,
    }));

    setColorInputErrors((prev) => ({
      ...prev,
      [colorKey]: '',
    }));
  };

  const handleColorHexInputChange = (colorKey) => (event) => {
    const rawValue = event.target.value.trim().toUpperCase();
    if (!hexInputPattern.test(rawValue)) {
      return;
    }
    setErrorMessage('');

    const normalizedValue = rawValue === '' || rawValue.startsWith('#') ? rawValue : `#${rawValue}`;

    setColorHexInputs((prev) => ({
      ...prev,
      [colorKey]: normalizedValue,
    }));

    if (fullHexPattern.test(normalizedValue)) {
      setFormData((prev) => ({
        ...prev,
        colors: {
          ...prev.colors,
          [colorKey]: normalizedValue,
        },
      }));
      setColorInputErrors((prev) => ({
        ...prev,
        [colorKey]: '',
      }));
      return;
    }

    setColorInputErrors((prev) => ({
      ...prev,
      [colorKey]: normalizedValue ? 'Enter a valid 6-digit hex code' : '',
    }));
  };

  const handleColorHexBlur = (colorKey) => () => {
    const currentValue = colorHexInputs[colorKey];
    if (fullHexPattern.test(currentValue)) {
      return;
    }

    const fallbackColor = formData.colors[colorKey].toUpperCase();
    setColorHexInputs((prev) => ({
      ...prev,
      [colorKey]: fallbackColor,
    }));

    setColorInputErrors((prev) => ({
      ...prev,
      [colorKey]: '',
    }));
  };

  const handleImageUpload = (imageType) => async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';

    if (!file) {
      return;
    }

    const adminID = user?.UserID ?? user?.userID;
    const dealerID = user?.DealerID ?? user?.dealerID;

    if (!adminID || !dealerID || !token) {
      setErrorMessage('User session expired. Please sign in again.');
      return;
    }

    setErrorMessage('');
    setImageUploadState((prev) => ({
      ...prev,
      [imageType]: {
        isUploading: true,
        fileName: file.name,
      },
    }));

    revokeLocalPreviewUrl(imageType);
    const localPreviewUrl = URL.createObjectURL(file);
    localObjectUrlsRef.current[imageType] = localPreviewUrl;
    setImagePreviewUrls((prev) => {
      if (imageType === 'logo') {
        return { ...prev, logo: localPreviewUrl };
      }

      return { ...prev, hero: localPreviewUrl };
    });

    try {
      const uploadResponse = await getDealerBrandingUploadUrl(file, dealerID, adminID, token);
      const uploadedKey = toNonEmptyString(uploadResponse?.imageKey ?? uploadResponse);
      const uploadedPreviewUrl = getPreviewableUrl(uploadResponse?.imageUrl);

      if (!uploadedKey) {
        throw new Error('Image key was not returned from upload API.');
      }

      setFormData((prev) => {
        if (imageType === 'logo') {
          return {
            ...prev,
            branding: {
              ...prev.branding,
              logo: uploadedKey,
            },
          };
        }

        return {
          ...prev,
          heroImage: uploadedKey,
        };
      });

      setImagePreviewUrls((prev) => {
        if (imageType === 'logo') {
          return { ...prev, logo: uploadedPreviewUrl || prev.logo };
        }

        return { ...prev, hero: uploadedPreviewUrl || prev.hero };
      });

      if (uploadedPreviewUrl) {
        revokeLocalPreviewUrl(imageType);
      }
    } catch (error) {
      setErrorMessage(getApiErrorMessage(error));
    } finally {
      setImageUploadState((prev) => ({
        ...prev,
        [imageType]: {
          ...prev[imageType],
          isUploading: false,
        },
      }));
    }
  };

  const handleSave = async () => {
    const adminID = user?.UserID ?? user?.userID;
    const dealerID = user?.DealerID ?? user?.dealerID;
    const isUploadingImage = imageUploadState.logo.isUploading || imageUploadState.hero.isUploading;

    if (isFetchingBranding) {
      setErrorMessage('Please wait until branding details are loaded.');
      return;
    }

    const hasColorInputError = Object.values(colorInputErrors).some((error) => Boolean(error));
    if (hasColorInputError) {
      setErrorMessage('Please provide valid hex codes for all colors.');
      return;
    }

    if (isUploadingImage) {
      setErrorMessage('Please wait until image upload is completed.');
      return;
    }

    if (!formData.branding.logo) {
      setErrorMessage('Logo image is required.');
      return;
    }

    if (!adminID || !dealerID || !token) {
      setErrorMessage('User session expired. Please sign in again.');
      return;
    }

    try {
      const payload = {
        primaryColor: formData.colors.primary,
        secondaryColor: formData.colors.secondary,
        accentColor: formData.colors.accent,
        backgroundColor: formData.colors.background,
        surfacecolor: formData.colors.surface,
        textColor: formData.colors.text,
        headingFont: formData.typography.heading,
        bodyFont: formData.typography.body,
        brandName: formData.branding.name,
        tagline: formData.branding.tagline,
        phoneNo: formData.branding.phone,
        email: formData.branding.email,
        address: formData.branding.address,
        customDomain: formData.branding.customDomain,
        logoUrl: extractImageKey(formData.branding.logo),
        heroImageUrl: extractImageKey(formData.heroImage),
      };

      await addDealerBranding(adminID, dealerID, payload, token);
      showDefaultAlert('success', 'Success', hasExistingBranding ? 'Branding page updated.' : 'Branding page saved.');
      setHasExistingBranding(true);
      setErrorMessage('');
    } catch (error) {
      setErrorMessage(getApiErrorMessage(error));
    }
  };

  return (
    <Grid container justifyContent="center">
      <Grid item xs={12} md={10} lg={8} sx={{ mt: { xs: 3, sm: 6 }, px: { xs: 2, sm: 0 }, mb: 3 }}>
        <Paper
          elevation={0}
          sx={{
            border: '1px solid #c52b2b',
            borderRadius: 2,
            overflow: 'hidden',
            backgroundColor: '#ffffff',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: { xs: 'flex-start', sm: 'center' },
              justifyContent: 'space-between',
              px: { xs: 2, sm: 3 },
              py: { xs: 1.5, sm: 2 },
              gap: 2,
              flexWrap: 'wrap',
              flexDirection: { xs: 'column', sm: 'row' },
              backgroundColor: '#c52b2b',
              color: '#ffffff',
            }}
          >
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Dealer Branding
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Configure logo, color palette, and typography
              </Typography>
            </Box>
            <Button
              variant="contained"
              onClick={() => navigate('/dealer-account')}
              sx={{
                backgroundColor: '#ffffff',
                color: '#c52b2b',
                border: '1px solid #ffffff',
                fontWeight: 700,
                width: { xs: '100%', sm: 'auto' },
                '&:hover': { backgroundColor: '#f7dede', borderColor: '#f7dede' },
              }}
            >
              Back to Account
            </Button>
          </Box>
          <Box sx={{ p: { xs: 2, sm: 3 } }}>
            {isFetchingBranding && (
              <Typography variant="body2" sx={{ mb: 1, color: '#4b5563' }}>
                Loading existing branding details...
              </Typography>
            )}
            {!isFetchingBranding && hasExistingBranding && (
              <Typography variant="body2" sx={{ mb: 1, color: '#4b5563' }}>
                Existing branding loaded. Update the fields and click Update.
              </Typography>
            )}
            <FormControl fullWidth margin="normal">
              <Button
                component="label"
                variant="outlined"
                color="primary"
                startIcon={<UploadFileIcon />}
                sx={{ justifyContent: 'flex-start' }}
                disabled={imageUploadState.logo.isUploading || isFetchingBranding}
              >
                {imageUploadState.logo.isUploading ? 'Uploading Logo...' : 'Upload Logo'}
                <input hidden type="file" accept="image/*" onChange={handleImageUpload('logo')} />
              </Button>
              <Typography variant="caption" sx={{ mt: 1, color: '#4b5563', display: 'block' }}>
                {imageUploadState.logo.fileName || (formData.branding.logo ? 'Logo uploaded' : 'No file selected')}
              </Typography>
              {!formData.branding.logo && (
                <Typography variant="caption" sx={{ mt: 0.5, color: '#d32f2f', display: 'block' }}>
                  *Required
                </Typography>
              )}
              {imagePreviewUrls.logo && (
                <Box sx={{ mt: 1.5 }}>
                  <Typography variant="caption" sx={{ color: '#4b5563', display: 'block', mb: 0.75 }}>
                    Logo Preview
                  </Typography>
                  <Box
                    component="img"
                    src={imagePreviewUrls.logo}
                    alt="Dealer logo preview"
                    sx={{
                      display: 'block',
                      width: '100%',
                      maxWidth: 260,
                      maxHeight: 140,
                      objectFit: 'contain',
                      borderRadius: 1,
                      border: '1px solid #e5e7eb',
                      backgroundColor: '#ffffff',
                      p: 1,
                    }}
                  />
                </Box>
              )}
            </FormControl>

            <TextField
              label="Brand Name"
              variant="outlined"
              fullWidth
              margin="normal"
              value={formData.branding.name}
              onChange={handleBrandingInputChange('name')}
              sx={inputSx}
            />
            <TextField
              label="Tagline"
              variant="outlined"
              fullWidth
              margin="normal"
              value={formData.branding.tagline}
              onChange={handleBrandingInputChange('tagline')}
              sx={inputSx}
            />
            <TextField
              label="Phone"
              variant="outlined"
              fullWidth
              margin="normal"
              value={formData.branding.phone}
              onChange={handleBrandingInputChange('phone')}
              sx={inputSx}
            />
            <TextField
              label="Email"
              variant="outlined"
              fullWidth
              margin="normal"
              value={formData.branding.email}
              onChange={handleBrandingInputChange('email')}
              sx={inputSx}
            />
            <TextField
              label="Address"
              variant="outlined"
              fullWidth
              margin="normal"
              multiline
              minRows={2}
              value={formData.branding.address}
              onChange={handleBrandingInputChange('address')}
              sx={inputSx}
            />
            <TextField
              label="Custom Domain (Optional)"
              variant="outlined"
              fullWidth
              margin="normal"
              placeholder="https://dealer.yourdomain.com"
              value={formData.branding.customDomain}
              onChange={handleBrandingInputChange('customDomain')}
              sx={inputSx}
            />

            {colorFields.map((field) => (
              <Box key={field.key} sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mt: 2 }}>
                <TextField
                  label={`${field.label} Color`}
                  variant="outlined"
                  type="color"
                  value={formData.colors[field.key]}
                  onChange={handleColorPickerChange(field.key)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ ...inputSx, width: 120 }}
                />
                <TextField
                  label={`${field.label} Hex Code`}
                  variant="outlined"
                  fullWidth
                  value={colorHexInputs[field.key]}
                  onChange={handleColorHexInputChange(field.key)}
                  onBlur={handleColorHexBlur(field.key)}
                  error={Boolean(colorInputErrors[field.key])}
                  helperText={colorInputErrors[field.key]}
                  placeholder="#000000"
                  sx={inputSx}
                />
              </Box>
            ))}

            <FormControl variant="outlined" fullWidth margin="normal" sx={inputSx}>
              <InputLabel>Heading Font</InputLabel>
              <Select
                value={formData.typography.heading}
                onChange={handleTypographyChange('heading')}
                label="Heading Font"
              >
                {fontPresets.map((preset) => (
                  <MenuItem key={preset.value} value={preset.value}>
                    {preset.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl variant="outlined" fullWidth margin="normal" sx={inputSx}>
              <InputLabel>Body Font</InputLabel>
              <Select
                value={formData.typography.body}
                onChange={handleTypographyChange('body')}
                label="Body Font"
              >
                {fontPresets.map((preset) => (
                  <MenuItem key={preset.value} value={preset.value}>
                    {preset.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth margin="normal">
              <Button
                component="label"
                variant="outlined"
                color="primary"
                startIcon={<UploadFileIcon />}
                sx={{ justifyContent: 'flex-start' }}
                disabled={imageUploadState.hero.isUploading || isFetchingBranding}
              >
                {imageUploadState.hero.isUploading ? 'Uploading Hero Image...' : 'Upload Hero Image (Optional)'}
                <input hidden type="file" accept="image/*" onChange={handleImageUpload('hero')} />
              </Button>
              <Typography variant="caption" sx={{ mt: 1, color: '#4b5563', display: 'block' }}>
                {imageUploadState.hero.fileName || (formData.heroImage ? 'Hero image uploaded' : 'No file selected')}
              </Typography>
              {imagePreviewUrls.hero && (
                <Box sx={{ mt: 1.5 }}>
                  <Typography variant="caption" sx={{ color: '#4b5563', display: 'block', mb: 0.75 }}>
                    Hero Image Preview
                  </Typography>
                  <Box
                    component="img"
                    src={imagePreviewUrls.hero}
                    alt="Dealer hero image preview"
                    sx={{
                      display: 'block',
                      width: '100%',
                      maxWidth: 420,
                      maxHeight: 220,
                      objectFit: 'cover',
                      borderRadius: 1,
                      border: '1px solid #e5e7eb',
                      backgroundColor: '#ffffff',
                    }}
                  />
                </Box>
              )}
            </FormControl>

            {errorMessage && (
              <Typography variant="body2" sx={{ mt: 1, color: '#d32f2f' }}>
                {errorMessage}
              </Typography>
            )}

            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between',
                gap: 2,
                marginTop: '16px',
              }}
            >
              <Button
                onClick={() => navigate('/dealer-account')}
                variant="outlined"
                color="primary"
                sx={{ marginTop: '10px', width: { xs: '100%', sm: 'auto' }, minWidth: { sm: 140 }, marginBottom: '5px' }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSave}
                disabled={isFetchingBranding || imageUploadState.logo.isUploading || imageUploadState.hero.isUploading}
                sx={{ marginTop: '10px', width: { xs: '100%', sm: 'auto' }, minWidth: { sm: 120 }, marginBottom: '5px' }}
              >
                {hasExistingBranding ? 'Update' : 'Save'}
              </Button>
            </Box>
          </Box>
        </Paper>
      </Grid>
    </Grid>
  );
};

export default DealerBrandingPage;
